import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase.config";
import dataCache from "../../services/DataCache";
import remoteQuizService from "../../services/RemoteQuizService";

export async function fetchAllQuizData(userId) {
  if (!userId) {
    return [];
  }

  try {
    let userData = dataCache.getUserProfile(userId);

    if (!userData) {
      const userProfileRef = doc(db, "public-profiles", userId);
      const userProfileDoc = await getDoc(userProfileRef);

      if (!userProfileDoc.exists()) {
        console.warn(
          "⚠️ User profile document does not exist in public-profiles collection"
        );
        console.log("💡 This could mean:");
        console.log("   - User hasn't completed onboarding");
        console.log("   - Profile document wasn't created properly");
        console.log("   - UserId is incorrect");
        return [];
      }

      console.log("✅ User profile document found");
      userData = userProfileDoc.data();
      dataCache.setUserProfile(userId, userData);
    }
    console.log("📄 Profile data fields:", Object.keys(userData));
    const quizData = [];

    // Get all quiz configs from remote
    const allQuizConfigs = await remoteQuizService.getAllQuizConfigs();
    if (!allQuizConfigs) {
      console.warn("⚠️ Could not fetch quiz configs");
      return [];
    }

    console.log("🔍 Checking for quiz IDs in profile...");
    for (const [quizType, config] of Object.entries(allQuizConfigs)) {
      // Skip non-core quizzes from the profile overview if configured
      if (config && config.excludeFromProfileOverview) {
        continue;
      }
      const quizId = userData[config.databaseField];

      console.log(`🔍 Checking ${quizType} quiz:`, {
        databaseField: config.databaseField,
        quizId: quizId || "Not found",
        configValid: !!config,
      });

      if (quizId) {
        try {
          const quizCacheKey = `quiz-data-${quizId}`;
          let quizDocData = dataCache.get(quizCacheKey);

          if (!quizDocData) {
            console.log(
              `📋 Fetching quiz document ${quizId} from quiz-answers...`
            );
            const quizDocRef = doc(db, "quiz-answers", quizId);
            const quizDoc = await getDoc(quizDocRef);

            if (!quizDoc.exists()) {
              console.warn(
                `⚠️ Quiz document ${quizId} not found in quiz-answers collection`
              );
              continue;
            }

            quizDocData = quizDoc.data();
            dataCache.set(quizCacheKey, quizDocData, 10 * 60 * 1000);
          }
          console.log(`📋 Quiz document ${quizId} data:`, {
            hasStatus: !!quizDocData.status,
            status: quizDocData.status,
            hasAnswers: !!quizDocData.answers,
            answersCount: quizDocData.answers
              ? Object.keys(quizDocData.answers).length
              : 0,
            hasInsights: !!quizDocData.insights,
          });

          if (quizDocData && quizDocData.status === "completed") {
            const quizAnswers = quizDocData.answers;
            const existingInsights = quizDocData.insights;

            console.log(`📊 Found ${quizType} quiz data:`, {
              quizId,
              hasAnswers: !!quizAnswers,
              hasInsights: !!existingInsights,
              insightsContent: existingInsights?.content
                ? "Present"
                : "Missing",
            });

            const results = Object.values(quizAnswers).map((answer) => ({
              field: answer.field,
              percentage1: answer.percentage,
            }));

            quizData.push({
              quizType,
              quizName: config.quizName,
              results,
              insights: existingInsights?.content || null,
              quizId,
              config,
            });
          } else if (quizDocData && quizDocData.status !== "completed") {
            console.warn(
              `⚠️ Quiz ${quizType} exists but status is: "${quizDocData.status}" (needs to be "completed")`
            );
          }
        } catch (err) {
          console.warn(`❌ Could not load quiz data for ${quizType}:`, err);
        }
      } else {
        console.log(
          `ℹ️ No ${quizType} quiz ID found in profile (field: ${config.databaseField})`
        );
      }
    }

    if (quizData.length === 0) {
      console.log(
        "💡 No completed quizzes found. To generate profile overview:"
      );
      console.log("   1. Complete at least one quiz");
      console.log("   2. Ensure quiz status is set to 'completed'");
      console.log("   3. Verify quiz ID is saved to profile document");
    }
    return quizData;
  } catch (err) {
    console.error("❌ Error fetching quiz data:", err);
    console.log("💡 This could indicate:");
    console.log("   - Database permission issues");
    console.log("   - Network connectivity problems");
    console.log("   - Firebase configuration issues");
    return [];
  }
}
